import { c as _c } from "react/compiler-runtime";
import { RefObject, createContext, useContext, useEffect, useRef } from 'react';
import { PageTypeOptions } from './getPageType';
import { consumeSuppressedSearchEvent } from './searchEventSuppression';
import trackSearchActions from './trackSearchActions';
export interface SearchTrackingResponse {
  readonly responseKey: string;
  readonly query?: string;
  // Overrides the ecommerce list id for the search event only, so view_item_list
  // and the other ecommerce events keep their own attribution.
  readonly listId?: string;
  readonly totalCount: number;
  readonly metadata?: ReadonlyArray<{
    readonly key: string;
    readonly value: string | null;
  } | null>;
  readonly pageTypeOptions: PageTypeOptions;
  readonly suppressSuggestionSearch?: boolean;
}
interface Props {
  readonly listId: string;
  readonly products: ReadonlyArray<{
    readonly code: string;
  }>;
  readonly response?: SearchTrackingResponse;
}

// A tabbed carousel owns this history so changing tabs does not replay the same response.
export const SearchTrackingHistoryContext = createContext<RefObject<Map<string, string | null>> | null>(null);

// Track the immutable loaded response, independently of visibility and live URL/store changes.
export function useSearchResultTracking(t0) {
  const $ = _c(17);
  const {
    listId,
    products,
    response
  } = t0;
  const lastTrackedResponse = useRef(null);
  const historyRef = useContext(SearchTrackingHistoryContext);
  let t1;
  if ($[0] !== response?.metadata) {
    t1 = Object.fromEntries((response?.metadata ?? []).flatMap(_temp));
    $[0] = response?.metadata;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const metadata = t1;
  const query = response?.query ?? "";
  const eventListId = response?.listId ?? listId;
  let items;
  let t2;
  if ($[2] !== eventListId || $[3] !== products || $[4] !== query || $[5] !== response) {
    items = products.map(_temp2);
    t2 = response ? JSON.stringify([response.responseKey, eventListId, query, response.totalCount, items]) : null;
    $[2] = eventListId;
    $[3] = products;
    $[4] = query;
    $[5] = response;
    $[6] = items;
    $[7] = t2;
  } else {
    items = $[6];
    t2 = $[7];
  }
  const responseIdentity = t2;
  let t3;
  let t4;
  if ($[8] !== eventListId || $[9] !== historyRef || $[10] !== items || $[11] !== metadata || $[12] !== query || $[13] !== response || $[14] !== responseIdentity) {
    t3 = () => {
      const history = historyRef?.current;
      if (response && responseIdentity !== lastTrackedResponse.current && (!history || history.get(response.responseKey) !== responseIdentity)) {
        lastTrackedResponse.current = responseIdentity;
        history?.set(response.responseKey, responseIdentity);
        if (!response.suppressSuggestionSearch || !consumeSuppressedSearchEvent()) {
          trackSearchActions(query, response.totalCount, response.pageTypeOptions, {
            ...metadata,
            query: metadata.query ?? query,
            totalCount: metadata.totalCount ?? String(response.totalCount),
            item_list_id: eventListId,
            items
          });
        }
      }
    };
    t4 = [eventListId, historyRef, items, metadata, query, response, responseIdentity];
    $[8] = eventListId;
    $[9] = historyRef;
    $[10] = items;
    $[11] = metadata;
    $[12] = query;
    $[13] = response;
    $[14] = responseIdentity;
    $[15] = t3;
    $[16] = t4;
  } else {
    t3 = $[15];
    t4 = $[16];
  }
  useEffect(t3, t4);
}
function _temp2(t0) {
  const {
    code
  } = t0;
  return {
    item_id: code
  };
}
function _temp(entry) {
  return entry?.value != null ? [[entry.key, entry.value]] : [];
}